// src/pages/Zips.tsx — per-ZIP, per-service price factors.
import { useCallback, useEffect, useState } from "react";
import { ops, type ZipFactor } from "../lib/ops";
import { useServices } from "../lib/context";
import { ChangePanel, type Pending } from "../components/ChangePanel";
import { Field, Loading, Notice, PageFrame } from "../components/ui";

const isZip = (z: string) => /^\d{5}$/.test(z.trim());
const validFactor = (t: string) => t.trim() !== "" && Number(t) >= 0.5 && Number(t) <= 2;

export default function Zips() {
  const services = useServices();
  const [zip, setZip] = useState("");
  const [shownZip, setShownZip] = useState<string | null>(null);
  const [rows, setRows] = useState<ZipFactor[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  const [pending, setPending] = useState<Pending | null>(null);

  const [addZip, setAddZip] = useState("");
  const [addSvc, setAddSvc] = useState("");
  const [addFactor, setAddFactor] = useState("");

  const load = useCallback(async (z: string | null) => {
    setRows(null);
    try {
      setRows(await ops.listZipFactors(z ?? undefined));
      setShownZip(z);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, []);
  useEffect(() => {
    void load(null);
  }, [load]);

  const propose = (z: string, serviceId: string, factor: number, name: string) =>
    setPending({
      change: { type: "zip_factor", zip: z, service_id: serviceId, zip_price_factor: factor },
      title: `${name} at ${z}: factor ${factor.toFixed(2)}`,
    });

  const panel = pending ? (
    <ChangePanel
      pending={pending}
      onClose={() => setPending(null)}
      onApplied={async () => {
        setEditing(null);
        await Promise.all([load(shownZip), services.refresh()]);
      }}
    />
  ) : undefined;

  const svcName = (id: string) => services.data?.services.find((s) => s.id === id)?.name ?? id;

  return (
    <PageFrame
      title="ZIP factors"
      lede="A service's price in a ZIP is multiplied by its factor for that ZIP. No row means 1.00."
      panel={panel}
    >
      <form
        className="zip-search"
        onSubmit={(e) => {
          e.preventDefault();
          if (isZip(zip)) void load(zip.trim());
        }}
      >
        <input
          type="search"
          className="search"
          inputMode="numeric"
          placeholder="Show every factor for a ZIP, e.g. 94105"
          value={zip}
          onChange={(e) => setZip(e.target.value)}
          aria-label="ZIP code"
        />
        <button type="submit" className="btn" disabled={!isZip(zip)}>
          Show ZIP
        </button>
        {shownZip && (
          <button
            type="button"
            className="btn quiet"
            onClick={() => {
              setZip("");
              void load(null);
            }}
          >
            Show all non-1.00 factors
          </button>
        )}
      </form>

      <p className="muted">
        {shownZip ? `Every factor stored for ${shownZip}.` : "Every factor that isn't 1.00, across all ZIPs."}
      </p>
      {!rows && !error && <Loading what="factors" />}
      {error && <Notice kind="error">{error}</Notice>}
      {rows && rows.length === 0 && (
        <p className="empty">{shownZip ? `No factors stored for ${shownZip}; every service is 1.00 there.` : "Every factor is 1.00."}</p>
      )}
      {rows && rows.length > 0 && (
        <div className="table-scroll">
          <table className="zip-table">
            <thead>
              <tr>
                <th scope="col">ZIP</th>
                <th scope="col">Service</th>
                <th scope="col">Factor</th>
                <th scope="col">Change</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const key = `${r.zip}|${r.service_id}`;
                const on = editing === key;
                return (
                  <tr key={key}>
                    <td className="mono">{r.zip}</td>
                    <td>
                      {r.service_name ?? r.service_id} <span className="muted">{r.service_id}</span>
                    </td>
                    <td className="mono">
                      {on ? (
                        <input
                          className="inline-input"
                          inputMode="decimal"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          aria-label={`New factor for ${r.service_id} at ${r.zip}`}
                        />
                      ) : (
                        Number(r.zip_price_factor).toFixed(2)
                      )}
                    </td>
                    <td>
                      {on ? (
                        <button
                          type="button"
                          className="btn primary"
                          disabled={!validFactor(editValue) || Number(editValue) === Number(r.zip_price_factor)}
                          onClick={() => propose(r.zip, r.service_id, Number(editValue), r.service_name ?? r.service_id)}
                        >
                          Preview
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="btn"
                          onClick={() => {
                            setEditing(key);
                            setEditValue(String(r.zip_price_factor));
                          }}
                        >
                          Edit factor
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <section className="editor-section card">
        <h2 className="card-title">Add a factor</h2>
        <div className="fields">
          <Field label="ZIP" htmlFor="add-zip">
            <input id="add-zip" inputMode="numeric" value={addZip} onChange={(e) => setAddZip(e.target.value)} />
          </Field>
          <Field label="Service" htmlFor="add-svc">
            <select id="add-svc" value={addSvc} onChange={(e) => setAddSvc(e.target.value)}>
              <option value="">Choose a service</option>
              {(services.data?.services ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} ({s.id})
                </option>
              ))}
            </select>
          </Field>
          <Field label="Factor" htmlFor="add-f" hint="0.50 to 2.00">
            <input id="add-f" inputMode="decimal" value={addFactor} onChange={(e) => setAddFactor(e.target.value)} />
          </Field>
        </div>
        <button
          type="button"
          className="btn primary"
          disabled={!isZip(addZip) || !addSvc || !validFactor(addFactor)}
          onClick={() => propose(addZip.trim(), addSvc, Number(addFactor), svcName(addSvc))}
        >
          Preview new factor
        </button>
      </section>
    </PageFrame>
  );
}
